import React from 'react';
import { Row, Col, Card, Spinner, Badge, Tooltip, OverlayTrigger, Table } from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  ComposedChart
} from 'recharts';
import {
  FaUsers,
  FaMoneyBillWave,
  FaChartLine,
  FaChartBar,
  FaChartPie,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaBriefcase,
  FaBuilding,
  FaUmbrellaBeach,
  FaUserTag,
  FaClock,
  FaCalendarTimes,
  FaHeartbeat
} from 'react-icons/fa';

/**
 * Componente para el dashboard de analytics de planillas premium
 */
const PlanillasAnalytics = ({
  estadisticas,
  datosGraficoEstados,
  datosGraficoTendencias,
  datosGraficoTipos,
  datosDesgloseCostos,
  datosDepartamentos,
  datosContratos,
  datosHE,
  datosAusentismo,
  datosPulsoDiario,
  loadingDetalles
}) => {

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Tarjeta de KPI
  const KPICard = ({ title, value, subValue, icon: Icon, color, trend, trendValue, tooltipContent }) => {
    const cardContent = (
      <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px", transition: "transform 0.2s" }}>
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className={`p-3 rounded-3 bg-${color} bg-opacity-10 text-${color}`}>
              <Icon size={24} />
            </div>
            {trend && (
              <Badge bg={trend === 'up' ? 'success' : 'danger'} pill className="d-flex align-items-center gap-1">
                {trend === 'up' ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                {trendValue}
              </Badge>
            )}
          </div>
          <h6 className="text-secondary mb-1 fw-normal">{title}</h6>
          <h3 className="fw-bold mb-1">{value}</h3>
          {subValue && <small className="text-muted">{subValue}</small>}
        </Card.Body>
      </Card>
    );

    if (tooltipContent) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-${title}`}>{tooltipContent}</Tooltip>}
        >
          <div className="h-100">{cardContent}</div>
        </OverlayTrigger>
      );
    }

    return cardContent;
  };

  return (
    <div className="analytics-dashboard">
      {/* 1. Indicadores Clave (KPIs) en la Cabecera */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0 fw-bold">Indicadores de Nómina</h5>
        <Badge bg="light" text="dark" className="p-2 border">
          Periodo Actual: {datosGraficoTendencias[datosGraficoTendencias.length - 1]?.fullMes || 'N/A'}
        </Badge>
      </div>
      
      <Row className="g-4 mb-5">
        <Col xl={3} md={6}>
          <KPICard
            title="Gasto Total Nómina"
            value={formatCurrency(estadisticas.gastoActual)}
            subValue={`Vs mes anterior: ${formatCurrency(estadisticas.gastoAnterior)}`}
            icon={FaMoneyBillWave}
            color="primary"
            trend={estadisticas.variacionGasto > 0 ? 'up' : 'down'}
            trendValue={formatPercent(Math.abs(estadisticas.variacionGasto))}
            tooltipContent={`Costo Promedio por Empleado: ${formatCurrency(estadisticas.costoPromedio)}`}
          />
        </Col>
        <Col xl={3} md={6}>
          <KPICard
            title="Headcount Activo"
            value={estadisticas.hcActual}
            subValue={`Variación: ${estadisticas.variacionHC > 0 ? '+' : ''}${estadisticas.variacionHC} emp.`}
            icon={FaUsers}
            color="success"
            trend={estadisticas.variacionHC > 0 ? 'up' : estadisticas.variacionHC < 0 ? 'down' : null}
            trendValue={Math.abs(estadisticas.variacionHC)}
            tooltipContent={
              <div className="text-start">
                <strong>Por Tipo de Contrato:</strong>
                {datosContratos && datosContratos.map((c, i) => (
                  <div key={i}>{c.name}: {c.value}</div>
                ))}
              </div>
            }
          />
        </Col>
        <Col xl={3} md={6}>
          <KPICard
            title="Días de Vacaciones"
            value={`${estadisticas.totalDiasVacaciones} días`}
            subValue={`Faltas: ${estadisticas.totalDiasFaltas} días`}
            icon={FaUmbrellaBeach}
            color="info"
            tooltipContent="Total acumulado en el último periodo procesado"
          />
        </Col>
        <Col xl={3} md={6}>
          <KPICard
            title="Costo Horas Extras"
            value={formatCurrency(estadisticas.totalCostoHE)}
            subValue={`${estadisticas.totalHorasHE.toFixed(1)} hrs totales`}
            icon={FaClock}
            color="warning"
            tooltipContent="Monto total pagado por HE 25%, 35% y 100% en el periodo"
          />
        </Col>
      </Row>

      {/* 2. Visualizaciones Principales */}
      <Row className="g-4">
        {/* A. Desglose de Costos */}
        <Col lg={4} md={12}>
          <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaChartPie className="text-primary" />
                </div>
                <h6 className="mb-0 fw-bold">Composición de Costos</h6>
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-center">
              {loadingDetalles ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Cargando desglose...</p>
                </div>
              ) : datosDesgloseCostos && datosDesgloseCostos.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={datosDesgloseCostos}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {datosDesgloseCostos.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                      <Legend verticalAlign="bottom" height={36}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {datosDesgloseCostos.map((item, idx) => (
                      <div key={idx} className="d-flex justify-content-between mb-2 small">
                        <span><span className="me-2" style={{ color: item.color }}>●</span>{item.name}</span>
                        <span className="fw-bold">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-5 text-muted">No hay datos suficientes para el desglose.</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* B. Tendencia Mensual */}
        <Col lg={8} md={12}>
          <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaChartLine className="text-success" />
                </div>
                <h6 className="mb-0 fw-bold">Evolución del Gasto de Nómina</h6>
              </div>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={datosGraficoTendencias}>
                  <defs>
                    <linearGradient id="colorCosto" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#666' }} minTickGap={10} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickFormatter={(val) => `S/ ${val / 1000}k`}
                  />
                  <RechartsTooltip
                    labelStyle={{ fontWeight: 'bold' }}
                    contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    formatter={(val) => formatCurrency(val)}
                  />
                  <Area
                    type="monotone"
                    dataKey="costoTotal"
                    stroke="#0d6efd"
                    fillOpacity={1}
                    fill="url(#colorCosto)"
                    name="Costo Laboral Total"
                    strokeWidth={3}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalNeto"
                    stroke="#ffc107"
                    name="Total Neto (Pagado)"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 3. Análisis Detallado de Horas Extras y Ausentismo */}
      <Row className="mt-4 g-4" style={{ color: "#8B4513" }}>
        {/* Horas Extras */}
        <Col lg={6} md={12}>
          <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaClock className="text-info" />
                </div>
                <h6 className="mb-0 fw-bold">Análisis de Horas Extras</h6>
              </div>
            </Card.Header>
            <Card.Body>
              {datosHE && datosHE.some(h => h.horas > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={datosHE}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} minTickGap={5} />
                      <YAxis yAxisId="left" orientation="left" axisLine={false} tickLine={false} stroke="#8B4513" name="Soles" />
                      <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} stroke="#0dcefd" name="Horas" />
                      <RechartsTooltip formatter={(val, name) => name === "Valor (S/)" ? formatCurrency(val) : `${val}h`} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="monto" name="Valor (S/)" fill="#8B4513" radius={[4, 4, 0, 0]} barSize={40} />
                      <Line yAxisId="right" type="monotone" dataKey="horas" name="Cantidad (H)" stroke="#0dcefd" strokeWidth={3} dot={{ r: 6 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="mt-3 bg-light p-2 rounded small">
                    <Table borderless size="sm" className="mb-0">
                      <thead><tr className="border-bottom"><th>Tipo</th><th className="text-end">Horas</th><th className="text-end">Monto</th></tr></thead>
                      <tbody>
                        {datosHE.map((h, i) => (
                          <tr key={i}><td>{h.name}</td><td className="text-end">{h.horas.toFixed(1)}h</td><td className="text-end fw-bold">{formatCurrency(h.monto)}</td></tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : <div className="text-center py-5 text-muted">Sin datos de horas extras.</div>}
            </Card.Body>
          </Card>
        </Col>

        {/* Ausentismo */}
        <Col lg={6} md={12}>
          <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaCalendarTimes className="text-danger" />
                </div>
                <h6 className="mb-0 fw-bold">Análisis de Ausentismo</h6>
              </div>
            </Card.Header>
            <Card.Body>
              {datosAusentismo && datosAusentismo.some(a => a.dias > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={datosAusentismo}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} minTickGap={5} />
                      <YAxis yAxisId="left" orientation="left" axisLine={false} tickLine={false} stroke="#e74c3c" />
                      <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} stroke="#ffc107" />
                      <RechartsTooltip formatter={(val, name) => name === "Costo/Dcto (S/)" ? formatCurrency(val) : `${val}d`} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="monto" name="Costo/Dcto (S/)" fill="#e74c3c" radius={[4, 4, 0, 0]} barSize={40} />
                      <Line yAxisId="right" type="monotone" dataKey="dias" name="Cantidad (D)" stroke="#ffc107" strokeWidth={3} dot={{ r: 6 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="mt-3 bg-light p-2 rounded small">
                    <Table borderless size="sm" className="mb-0">
                      <thead><tr className="border-bottom"><th>Tipo</th><th className="text-end">Días</th><th className="text-end">Costo/Dcto</th></tr></thead>
                      <tbody>
                        {datosAusentismo.map((a, i) => (
                          <tr key={i}><td>{a.name}</td><td className="text-end">{a.dias.toFixed(1)}d</td><td className="text-end fw-bold">{formatCurrency(a.monto)}</td></tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : <div className="text-center py-5 text-muted">Sin datos de ausentismo.</div>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 4. Pulso Mensual de Ocurrencias (Daily Drilldown) */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaHeartbeat className="text-danger" />
                </div>
                <h6 className="mb-0 fw-bold">Pulso Diario de Ocurrencias (Horas/Días)</h6>
              </div>
            </Card.Header>
            <Card.Body>
              {datosPulsoDiario && datosPulsoDiario.some(p => p.faltas > 0 || p.he > 0 || p.vacaciones > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={datosPulsoDiario}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="dia" axisLine={false} tickLine={false} minTickGap={2} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="he" name="Horas Extra (hrs)" stackId="a" fill="#0dcefd" />
                    <Bar dataKey="faltas" name="Faltas (cant)" stackId="a" fill="#dc3545" />
                    <Bar dataKey="vacaciones" name="Vacaciones (cant)" stackId="a" fill="#ffc107" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">No se registraron ocurrencias diarias en este periodo.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 5. Distribución por Departamentos y Contratos */}
      <Row className="mt-4 g-4">
        {/* Departamentos */}
        <Col lg={7} md={12}>
          <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaBuilding className="text-warning" />
                </div>
                <h6 className="mb-0 fw-bold">Gasto por Departamento</h6>
              </div>
            </Card.Header>
            <Card.Body>
              {loadingDetalles ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="warning" />
                </div>
              ) : datosDepartamentos && datosDepartamentos.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={datosDepartamentos} layout="vertical" margin={{ left: 20, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#eee" />
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      width={100}
                      tick={{ fontSize: 13, fontWeight: 500 }}
                    />
                    <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                    <Bar
                      dataKey="total"
                      fill="#8B4513"
                      radius={[0, 10, 10, 0]}
                      barSize={20}
                      name="Gasto Total"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">Sin datos por departamento.</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Contratos o Tipos de Planilla */}
        <Col lg={5} md={12}>
          <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center">
                <div className="p-2 rounded-2 bg-light me-2">
                  <FaUserTag className="text-brown" style={{ color: "#8B4513" }} />
                </div>
                <h6 className="mb-0 fw-bold">Distribución por Tipo de Contrato</h6>
              </div>
            </Card.Header>
            <Card.Body>
              {datosContratos && datosContratos.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={datosContratos}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} minTickGap={5} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip />
                    <Bar dataKey="value" name="Empleados" fill="#FD7E14" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">Sin datos de contratos.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PlanillasAnalytics;